// CAPABILITY: Public viewer tracking transport → trackAnalyticsEvent Cloud Function.
// Requests use Content-Type text/plain: a CORS "simple request", so the browser sends NO preflight OPTIONS
// (each preflight is a separate paid function invocation). The function parses the JSON string body.
import app from "../../firebase";

function getDefaultTrackUrl() {
  const projectId = app?.options?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID;
  return projectId ? `https://us-central1-${projectId}.cloudfunctions.net/trackAnalyticsEvent` : "";
}

function logAnalyticsFailure(type, error) {
  const code = error?.code || error?.name || error?.message || "unknown";
  console.warn("ANALYTICS_EVENT_SEND_FAILED", `type=${type} code=${code}`);
}

export function getAnalyticsTrackUrl() {
  return process.env.REACT_APP_ANALYTICS_TRACK_URL || getDefaultTrackUrl();
}

export async function sendAnalyticsEvent(payload, { useBeacon = false } = {}) {
  const trackUrl = getAnalyticsTrackUrl();
  if (!trackUrl) {
    console.info("ANALYTICS_TRACKING_DISABLED", `type=${payload?.type || "unknown"} reason=missing_track_url`);
    return { ok: false, disabled: true };
  }

  const body = JSON.stringify(payload);

  if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const sent = navigator.sendBeacon(trackUrl, new Blob([body], { type: "text/plain;charset=UTF-8" }));
    if (sent) return { ok: true, beacon: true };
  }

  try {
    const response = await fetch(trackUrl, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      credentials: "omit",
      keepalive: useBeacon,
    });
    if (!response.ok) throw new Error(`HTTP_${response.status}`);
    return response.json().catch(() => ({ ok: true }));
  } catch (error) {
    logAnalyticsFailure(payload?.type || "unknown", error);
    return { ok: false, error };
  }
}

export async function sendSessionStart({ sessionId, viewerId, pageId, stage, signal }) {
  const result = await sendAnalyticsEvent({ type: "SESSION_START", sessionId, viewerId, pageId, stage, signal });
  console.info(
    result.ok ? "ANALYTICS_SESSION_START_SENT" : "ANALYTICS_SESSION_START_FAILED",
    `page_id=${pageId} session_id=${sessionId} stage=${stage || "unknown"} signal=${signal || "unknown"}`
  );
  return result;
}

export async function sendHeartbeat({ sessionId, viewerId, pageId, heartbeatSeq, isPlaying, watchSeconds, stage, signal }, { useBeacon = false } = {}) {
  const result = await sendAnalyticsEvent(
    { type: "HEARTBEAT", sessionId, viewerId, pageId, heartbeatSeq, isPlaying: Boolean(isPlaying), watchSeconds, stage, signal },
    { useBeacon }
  );
  console.info(
    result.ok ? "ANALYTICS_HEARTBEAT_SENT" : "ANALYTICS_HEARTBEAT_FAILED",
    `page_id=${pageId} session_id=${sessionId} seq=${heartbeatSeq} is_playing=${Boolean(isPlaying)} watch_seconds=${watchSeconds ?? 0} stage=${stage || "unknown"}`
  );
  return result;
}

export function sendSessionEnd({ sessionId, viewerId, pageId, isPlaying, watchSeconds, stage, signal }) {
  const result = sendAnalyticsEvent(
    { type: "SESSION_END", sessionId, viewerId, pageId, isPlaying: Boolean(isPlaying), watchSeconds, stage, signal },
    { useBeacon: true }
  );
  Promise.resolve(result).then((payload) => {
    console.info(
      payload?.ok ? "ANALYTICS_SESSION_END_SENT" : "ANALYTICS_SESSION_END_FAILED",
      `page_id=${pageId} session_id=${sessionId} watch_seconds=${watchSeconds ?? 0}`
    );
  });
  return result;
}
